import { SCHEMAS, DEFAULT_VERSIONS } from "./schemaRegistry";
import { validateEntity } from "./validationService";
import { migrateEntity } from "./migrationService";
import { adminAuditService } from "../adminAuditService";
import { auditService } from "../auditService";
import { logger, LOG_CATEGORIES } from "../../utils/logger";
import { collection, query, orderBy, where, getDocs, limit } from "firebase/firestore";
import { db } from "../firebase.js";

class SerializationService {
    constructor() {
        this.serviceName = "serializationService";
    }

    /**
     * Serialize an entity with version control and audit logging
     * @param {Object} entity - The entity to serialize
     * @param {string} entityType - Type of entity (User, Opportunity, etc.)
     * @param {string} version - Target version for serialization
     * @param {Object} context - Context information for audit logging
     * @returns {Object} Serialized entity
     */
    async serialize(entity, entityType, version = null, context = {}) {
        try {
            const targetVersion = version || DEFAULT_VERSIONS[entityType];
            
            if (!targetVersion) {
                throw new Error(`No default version defined for entity type: ${entityType}`);
            }

            // Validate entity structure
            await validateEntity(entity, entityType, targetVersion);

            // Migrate to target version if needed
            const migratedEntity = await migrateEntity(entity, entityType, targetVersion);

            // Create serialized entity
            const serializedEntity = {
                ...migratedEntity,
                version: targetVersion,
                serializedAt: new Date().toISOString(),
                serializedBy: context.userId || "system",
                changeReason: context.changeReason || "serialization",
                regulatoryRequirement: this.getRegulatoryRequirement(entityType, targetVersion)
            };

            // Log serialization event
            await this.logSerializationEvent(entityType, serializedEntity, context);

            // Log to admin audit if it's an admin action
            if (context.isAdminAction) {
                await adminAuditService.logAdminAction(
                    context.userId,
                    "entity_serialized",
                    {
                        targetEntityType: entityType,
                        targetEntityId: entity.id,
                        oldValue: entity,
                        newValue: serializedEntity,
                        version: targetVersion,
                        reason: context.changeReason,
                        category: "serialization",
                        regulatoryRequirement: serializedEntity.regulatoryRequirement
                    }
                );
            }

            return serializedEntity;

        } catch (error) {
            await logger.error(LOG_CATEGORIES.SERIALIZATION, "Serialization failed:", error, {
                entityType,
                entityId: entity?.id,
                version,
                context
            });
            throw error;
        }
    }

    /**
     * Deserialize an entity with version validation
     * @param {Object} serializedEntity - The serialized entity
     * @param {string} entityType - Type of entity
     * @param {Object} context - Context information
     * @returns {Object} Deserialized entity
     */
    async deserialize(serializedEntity, entityType, context = {}) {
        try {
            const version = serializedEntity.version;
            
            if (!version) {
                throw new Error("Serialized entity missing version information");
            }

            // Validate serialized entity
            await validateEntity(serializedEntity, entityType, version);

            // Log deserialization event
            await this.logDeserializationEvent(entityType, serializedEntity, context);

            // Return entity without serialization metadata
            // eslint-disable-next-line no-unused-vars
            const { version: _, serializedAt, serializedBy, changeReason, regulatoryRequirement, ...deserializedEntity } = serializedEntity;

            return deserializedEntity;

        } catch (error) {
            await logger.error(LOG_CATEGORIES.SERIALIZATION, "Deserialization failed:", error, {
                entityType,
                entityId: serializedEntity?.id,
                context
            });
            throw error;
        }
    }

    /**
     * Get entities by type from Firestore
     * @param {string} entityType - Type of entity to retrieve
     * @param {Object} options - Query options
     * @returns {Promise<Array>} Array of entities
     */
    async getEntitiesByType(entityType, options = {}) {
        try {
            const { limit: limitCount = 100, orderBy: orderByField = "createdAt", direction = "desc" } = options;
            
            // Query entities from Firestore
            const entitiesRef = collection(db, entityType.toLowerCase() + "s");
            const q = query(
                entitiesRef, 
                orderBy(orderByField, direction),
                limit(limitCount)
            );
            
            const snapshot = await getDocs(q);
            const entities = [];
            
            snapshot.forEach((doc) => {
                entities.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            
            await logger.info(LOG_CATEGORIES.SERIALIZATION, "Entities retrieved by type", {
                entityType,
                count: entities.length
            });
            
            return entities;
            
        } catch (error) {
            await logger.error(LOG_CATEGORIES.SERIALIZATION, "Failed to get entities by type", error, {
                entityType,
                options
            });
            throw error;
        }
    }

    /**
     * Get all entities with serialization status
     * @param {Object} options - Query options
     * @returns {Promise<Array>} Array of entities with serialization metadata
     */
    async getAllEntities(options = {}) {
        try {
            const { limit: limitCount = 1000 } = options;
            const allEntities = [];
            
            // Define entity types to process
            const entityTypes = ["User", "Opportunity", "Investment", "Transaction", "KYC", "Notification", "Message", "Agent", "Document"];
            
            for (const entityType of entityTypes) {
                try {
                    const entities = await this.getEntitiesByType(entityType, { limit: limitCount });
                    
                    // Add serialization metadata to each entity
                    const entitiesWithMetadata = entities.map(entity => {
                        const hasSerializationFields = entity.status === "serialized" && 
                                                     entity.version && 
                                                     entity.serializedAt && 
                                                     entity.serializedBy;
                        
                        return {
                            ...entity,
                            type: entityType,
                            status: hasSerializationFields ? "serialized" : "needs_migration",
                            lastUpdated: entity.updatedAt || entity.createdAt || new Date().toISOString(),
                            validationErrors: 0, // Will be calculated during validation
                            serializedBy: entity.serializedBy || "system"
                        };
                    });
                    
                    allEntities.push(...entitiesWithMetadata);
                    
                    await logger.info(LOG_CATEGORIES.SERIALIZATION, "Entities processed for type", {
                        entityType,
                        count: entitiesWithMetadata.length,
                        serialized: entitiesWithMetadata.filter(e => e.status === "serialized").length
                    });
                    
                } catch (error) {
                    await logger.warn(LOG_CATEGORIES.SERIALIZATION, "Failed to get entities for type", {
                        entityType,
                        error: error.message
                    });
                }
            }
            
            await logger.info(LOG_CATEGORIES.SERIALIZATION, "All entities retrieved", {
                totalCount: allEntities.length,
                serializedCount: allEntities.filter(e => e.status === "serialized").length
            });
            
            return allEntities;
            
        } catch (error) {
            await logger.error(LOG_CATEGORIES.SERIALIZATION, "Failed to get all entities", error);
            throw error;
        }
    }

    /**
     * Get version history for an entity
     * @param {string} entityId - Entity ID
     * @param {string} entityType - Entity type
     * @returns {Promise<Array>} Version history
     */
    async getVersionHistory(entityId, entityType) {
        try {
            // Query version history from audit logs
            const historyRef = collection(db, "audit_logs");
            const q = query(
                historyRef,
                where("entityType", "==", entityType),
                where("entityId", "==", entityId),
                where("action", "in", ["entity_serialized", "entity_updated"]),
                orderBy("timestamp", "desc")
            );
            
            const snapshot = await getDocs(q);
            const history = [];
            
            snapshot.forEach((doc) => {
                const data = doc.data();
                history.push({
                    version: data.version || "v1.0.0",
                    timestamp: data.timestamp,
                    serializedBy: data.userId || "system",
                    changeReason: data.changeReason || "update",
                    changes: data.changes || {},
                    regulatoryRequirement: data.regulatoryRequirement
                });
            });
            
            await logger.info(LOG_CATEGORIES.SERIALIZATION, "Version history retrieved", {
                entityType,
                entityId,
                versions: history.length
            });
            
            return history;
            
        } catch (error) {
            await logger.error(LOG_CATEGORIES.SERIALIZATION, "Failed to get version history", error, {
                entityType,
                entityId
            });
            throw error;
        }
    }

    /**
     * Compare two versions of an entity
     * @param {Object} oldVersion - Old version of entity
     * @param {Object} newVersion - New version of entity
     * @returns {Object} Comparison result
     */
    compareVersions(oldVersion, newVersion) {
        try {
            const changes = {
                added: {},
                modified: {},
                removed: {},
                summary: {
                    fieldsChanged: 0,
                    fieldsAdded: 0,
                    fieldsRemoved: 0
                }
            };
            
            // Get all unique keys from both versions
            const allKeys = new Set([
                ...Object.keys(oldVersion || {}),
                ...Object.keys(newVersion || {})
            ]);
            
            allKeys.forEach(key => {
                const oldValue = oldVersion?.[key];
                const newValue = newVersion?.[key];
                
                if (oldValue === undefined && newValue !== undefined) {
                    // Field added
                    changes.added[key] = newValue;
                    changes.summary.fieldsAdded++;
                } else if (oldValue !== undefined && newValue === undefined) {
                    // Field removed
                    changes.removed[key] = oldValue;
                    changes.summary.fieldsRemoved++;
                } else if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
                    // Field modified
                    changes.modified[key] = {
                        old: oldValue,
                        new: newValue
                    };
                    changes.summary.fieldsChanged++;
                }
            });
            
            return changes;
            
        } catch (error) {
            // Log error without await since this is not an async method
            logger.error(LOG_CATEGORIES.SERIALIZATION, "Version comparison failed", error);
            throw error;
        }
    }

    /**
     * Get regulatory requirement for entity type and version
     * @param {string} entityType - Entity type
     * @param {string} version - Entity version
     * @returns {string} Regulatory requirement
     */
    getRegulatoryRequirement(entityType, version) {
        const schema = SCHEMAS[entityType]?.[version.replace(/\./g, "_")];
        return schema?.audit?.regulatoryRequirement || "general_compliance";
    }

    /**
     * Log serialization event
     * @param {string} entityType - Entity type
     * @param {Object} serializedEntity - Serialized entity
     * @param {Object} context - Context information
     */
    async logSerializationEvent(entityType, serializedEntity, context) {
        await auditService.logUserAction(
            context.userId || "system",
            "entity_serialized",
            {
                entityType,
                entityId: serializedEntity.id,
                version: serializedEntity.version,
                changeReason: serializedEntity.changeReason,
                regulatoryRequirement: serializedEntity.regulatoryRequirement,
                ipAddress: context.ipAddress,
                userAgent: context.userAgent
            }
        );
    }

    /**
     * Log deserialization event
     * @param {string} entityType - Entity type
     * @param {Object} serializedEntity - Serialized entity
     * @param {Object} context - Context information
     */
    async logDeserializationEvent(entityType, serializedEntity, context) {
        await auditService.logUserAction(
            context.userId || "system",
            "entity_deserialized",
            {
                entityType,
                entityId: serializedEntity.id,
                version: serializedEntity.version,
                ipAddress: context.ipAddress,
                userAgent: context.userAgent
            }
        );
    }

    /**
     * Get available versions for an entity type
     * @param {string} entityType - Entity type
     * @returns {Array<string>} Available versions
     */
    getAvailableVersions(entityType) {
        const schema = SCHEMAS[entityType];
        if (!schema) {
            return [];
        }
        
        return Object.keys(schema).map(version => 
            version.replace(/_/g, ".")
        );
    }

    /**
     * Check if entity needs migration
     * @param {Object} entity - Entity to check
     * @param {string} entityType - Entity type
     * @returns {boolean} Whether migration is needed
     */
    needsMigration(entity, entityType) {
        const currentVersion = entity.version || "v1.0.0";
        const targetVersion = DEFAULT_VERSIONS[entityType];
        
        return currentVersion !== targetVersion;
    }
}

// Export singleton instance
export const serializationService = new SerializationService(); 